import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Badge } from "@/components/common/DataTable";
import { customerService } from "@/services";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis,
} from "recharts";
import { Users, Contact as ContactIcon, CheckCircle2, Layers, Loader2, HeartPulse } from "lucide-react";

const PIE_COLORS = ["#0d9488", "#06b6d4", "#f59e0b", "#8b5cf6", "#ec4899", "#64748b"];

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm animate-rise" data-testid={`c360-kpi-${label}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <p className="text-3xl font-bold text-foreground mt-3">{value ?? "—"}</p>
    </div>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={testid}>
      <h3 className="font-semibold text-foreground mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function Customer360Page() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { customerService.insights().then(setData).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const t = data?.totals || {};
  const byType = data?.byType || [];
  const byStatus = data?.byStatus || [];
  const bySegment = (data?.bySegment || []).filter((s) => s.value > 0);
  const recent = data?.recent || [];

  return (
    <div data-testid="customer360-page">
      <PageHeader title="Customer 360" subtitle="Customer Intelligence Hub — a unified view across your customer base" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
        <Kpi label="Customers" value={t.customers} icon={Users} color="text-primary" />
        <Kpi label="Active" value={t.active} icon={CheckCircle2} color="text-emerald-500" />
        <Kpi label="Contacts" value={t.contacts} icon={ContactIcon} color="text-violet-500" />
        <Kpi label="Segments" value={t.segments} icon={Layers} color="text-sky-500" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <ChartCard title="Customers by Type" testid="c360-by-type">
          {byType.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={byType} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {byType.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Customers by Segment" testid="c360-by-segment">
          {bySegment.length === 0 ? <Empty text="No segment members yet" /> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={bySegment}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {bySegment.map((s, i) => <Cell key={i} fill={s.color || PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <ChartCard title="By Status" testid="c360-by-status">
          <div className="space-y-2.5">
            {byStatus.length === 0 && <Empty />}
            {byStatus.map((s) => (
              <div key={s.name} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{s.name}</span>
                <Badge color="teal">{s.value}</Badge>
              </div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Health Score (placeholder)" testid="c360-health">
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <HeartPulse className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">Health scoring activates in a later AI phase. All customers currently show <strong>Unknown</strong>.</p>
          </div>
        </ChartCard>

        <ChartCard title="Recent Customers" testid="c360-recent">
          <div className="space-y-2.5 max-h-56 overflow-y-auto">
            {recent.length === 0 && <Empty />}
            {recent.map((c) => (
              <button key={c.id} onClick={() => navigate(`/admin/customers/${c.id}`)}
                className="w-full flex items-center justify-between text-sm border-b border-border pb-2 hover:text-primary transition-colors text-left"
                data-testid={`c360-recent-${c.id}`}>
                <span className="font-medium text-foreground">{c.customerName}</span>
                <span className="text-xs text-muted-foreground">{c.customerType}</span>
              </button>
            ))}
          </div>
        </ChartCard>
      </div>
    </div>
  );
}

function Empty({ text = "No data yet" }) {
  return <p className="text-sm text-muted-foreground py-10 text-center">{text}</p>;
}
